import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { API_URL } from "@/utils/api";

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.replace("#", ""));
        const sessionId = params.get("session_id");

        if (!sessionId) {
          setError("No session ID found");
          setTimeout(() => navigate("/login"), 2000);
          return;
        }

        // Send session_id to backend
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId })
        });

        if (!response.ok) {
          throw new Error("Failed to process session");
        }

        const data = await response.json();
        const user = data.user;
        const token = data.access_token;

        // Store token with fallback for mobile Safari
        if (token) {
          for (let i = 0; i < 3; i++) {
            try {
              localStorage.setItem("auth_token", token);
              sessionStorage.setItem("auth_token_backup", token);
              break;
            } catch (storageError) {
              console.error(`Storage attempt ${i + 1} failed:`, storageError);
              await new Promise(r => setTimeout(r, 50));
            }
          }
        }

        // Clear the hash from URL
        window.history.replaceState(null, "", window.location.pathname);

        // Small delay for mobile storage sync
        await new Promise(r => setTimeout(r, 200));

        // Redirect based on role
        if (user.role === "admin") {
          navigate("/admin", { state: { user }, replace: true });
        } else if (user.role === "finance") {
          navigate("/finance", { state: { user }, replace: true });
        } else {
          navigate("/dashboard", { state: { user }, replace: true });
        }
      } catch (err) {
        console.error("Auth callback error:", err);
        setError(err.message);
        setTimeout(() => navigate("/login"), 2000);
      }
    };

    processSession();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-red-500 mb-2">Authentication failed</p>
          <p className="text-slate-500 text-sm">{error}</p>
          <p className="text-slate-400 text-xs mt-2">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
        <p className="text-slate-500 text-sm">Signing you in...</p>
      </div>
    </div>
  );
}
